import {api} from './api';

/**
 * 邀请码状态
 */
export type InvitationCodeStatus = 'ACTIVE' | 'INACTIVE';

/**
 * 邀请码响应
 */
export interface InvitationCode {
    id: number;
    code: string;
    roleCode?: string;
    maxUses: number;
    usedCount: number;
    remainingUses: number;
    status: InvitationCodeStatus;
    expireAt: string | null;
    available: boolean;
    remark: string | null;
    createdTime: string;
    updatedTime: string;
}

/**
 * 分页响应
 */
export interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

/**
 * 验证邀请码请求
 */
export interface ValidateInvitationCodeRequest {
    code: string;
}

/**
 * 验证邀请码响应
 */
export interface ValidateInvitationCodeResponse {
    valid: boolean;
    message: string;
    roleCode?: string;
}

/**
 * 创建邀请码请求
 */
export interface CreateInvitationCodeRequest {
    code?: string;
    roleCode?: string;
    maxUses?: number;
    expireAt?: string | null;
    remark?: string;
}

/**
 * 更新邀请码请求
 */
export interface UpdateInvitationCodeRequest {
    maxUses?: number;
    status?: InvitationCodeStatus;
    expireAt?: string | null;
    remark?: string;
}

/**
 * 用户信息
 */
export interface UserDto {
    userId: number;
    username: string;
    email: string;
    status: string;
    createdTime: string;
    updatedTime: string;
}

/**
 * 邀请码服务
 */
export const invitationCodeService = {
    /**
     * 验证邀请码
     */
    validate: async (code: string): Promise<ValidateInvitationCodeResponse> => {
        const response = await api.post('/api/v1/system/invitation-codes/validate', {code});
        return response.data.data;
    },

    /**
     * 获取邀请码列表（分页）
     */
    getList: async (page: number = 0, size: number = 10, keyword?: string): Promise<PageResponse<InvitationCode>> => {
        const params = new URLSearchParams({
            page: page.toString(),
            size: size.toString(),
        });
        if (keyword) {
            params.append('keyword', keyword);
        }
        const response = await api.get(`/api/v1/system/invitation-codes?${params.toString()}`);
        return response.data.data;
    },

    /**
     * 获取邀请码详情
     */
    getDetail: async (id: number): Promise<InvitationCode> => {
        const response = await api.get(`/api/v1/system/invitation-codes/${id}`);
        return response.data.data;
    },

    /**
     * 创建邀请码
     */
    create: async (request: CreateInvitationCodeRequest): Promise<InvitationCode> => {
        const response = await api.post('/api/v1/system/invitation-codes', request);
        return response.data.data;
    },

    /**
     * 更新邀请码
     */
    update: async (id: number, request: UpdateInvitationCodeRequest): Promise<InvitationCode> => {
        const response = await api.put(`/api/v1/system/invitation-codes/${id}`, request);
        return response.data.data;
    },

    /**
     * 删除邀请码
     */
    delete: async (id: number): Promise<void> => {
        await api.delete(`/api/v1/system/invitation-codes/${id}`);
    },

    /**
     * 获取邀请码关联的用户列表
     */
    getUsers: async (id: number): Promise<UserDto[]> => {
        const response = await api.get(`/api/v1/system/invitation-codes/${id}/users`);
        return response.data.data;
    },
};
